import gql from 'graphql-tag'
import * as React from 'react'
import Button from '../../../../common/src/components/Button'
import { BodyText, H4, Spacer } from '../../../../common/src/components/core'
import { stringifyAndRemoveNull } from '../../../../common/src/func'
import {
  FetchFirmDetailsQuery,
  FetchFirmEntrataInfoQuery,
  FetchFirmEntrataInfoQueryVariables,
  FetchFirmYardiInfoQuery,
  FetchFirmYardiInfoQueryVariables,
} from '../../../../common/src/types.gen'
import { apolloWebClient, NETWORK_ONLY_FETCH_POLICY } from '../../apolloClientForWeb'
import { Fragments } from '../../graphql/fragments'
import FirmCredsEditBox, { Tab } from './FirmCredsEditBox'

const fetchFirmEntrataInfo = gql`
  query FetchFirmEntrataInfo($firmId: Int!) {
    entrataInfo(firmId: $firmId) {
      ...EntrataInfo
    }
  }
  ${Fragments.entrataInfo}
`

const fetchFirmYardiInfo = gql`
  query FetchFirmYardiInfo($firmId: Int!) {
    yardiInfo(firmId: $firmId) {
      ...YardiInfo
    }
  }
  ${Fragments.yardiInfo}
`

const updateFirmResmanInfo = gql`
  mutation UpdateFirmResmanInfo($firmId: Int!, $resmanInfo: BasicPmInfoInput!) {
    updateFirmResmanInfo(firmId: $firmId, resmanInfo: $resmanInfo)
  }
`

const updateFirmApmInfo = gql`
  mutation UpdateFirmApmInfo($firmId: Int!, $apmInfo: ApmInfoInput!) {
    updateFirmApmInfo(firmId: $firmId, apmInfo: $apmInfo)
  }
`

const updateFirmEntrataInfo = gql`
  mutation UpdateFirmEntrataInfo($firmId: Int!, $entrataInfo: EntrataInfoInput!) {
    updateFirmEntrataInfo(firmId: $firmId, entrataInfo: $entrataInfo)
  }
`

const updateFirmYardiInfo = gql`
  mutation UpdateFirmYardiInfo($firmId: Int!, $yardiInfo: YardiInfoInput!) {
    updateFirmYardiInfo(firmId: $firmId, yardiInfo: $yardiInfo)
  }
`

interface PropertyManagementSystemsProps {
  firmInfo: FetchFirmDetailsQuery['firm']
}

interface PropertyManagementSystemsState {
  tab: Tab
  creds: string
}

export default class PropertyManagementSystems extends React.PureComponent<
  PropertyManagementSystemsProps,
  PropertyManagementSystemsState
> {
  state = {
    tab: Tab.APPFOLIO,
    creds: '{}',
  }

  componentDidMount() {
    this.setAppfolio()
  }

  render() {
    const { firmInfo } = this.props
    const { tab, creds } = this.state

    return (
      <>
        <H4>Property Management System</H4>
        <Spacer height={8} />
        <BodyText>
          Firm credentials to read units data and write guest cards. We hide passwords after you enter them for
          security.
        </BodyText>
        <Spacer height={8} />
        <Button size="small" color="sky" filled={tab === Tab.APPFOLIO} onClick={this.setAppfolio}>
          AppFolio
        </Button>
        <Spacer width={8} />
        <Button size="small" color="sky" filled={tab === Tab.YARDI} onClick={this.setYardi}>
          Yardi
        </Button>
        <Spacer width={8} />
        <Button size="small" color="sky" filled={tab === Tab.RESMAN} onClick={this.setResman}>
          ResMan
        </Button>
        <Spacer width={8} />
        <Button size="small" color="sky" filled={tab === Tab.ENTRATA} onClick={this.setEntrata}>
          Entrata
        </Button>
        <Spacer height={8} />
        <FirmCredsEditBox
          firmId={firmInfo.id}
          fetchKey={tab}
          creds={creds}
          fetchOnEdit={tab === Tab.ENTRATA || tab === Tab.YARDI ? this.fetchOnEdit : undefined}
          mutateOnSubmit={this.mutateOnSubmit}
        />
      </>
    )
  }

  private setAppfolio = () => {
    const apmInfo = this.props.firmInfo.apmInfoWithObfuscatedSecrets
    this.setState({
      tab: Tab.APPFOLIO,
      creds: stringifyAndRemoveNull(apmInfo),
    })
  }

  private setYardi = () => {
    const yardiInfo = this.props.firmInfo.yardiInfoWithObfuscatedSecrets
    this.setState({
      tab: Tab.YARDI,
      creds: stringifyAndRemoveNull(yardiInfo),
    })
  }

  private setResman = () => {
    const resmanInfo = this.props.firmInfo.resmanInfoWithObfuscatedSecrets
    this.setState({
      tab: Tab.RESMAN,
      creds: stringifyAndRemoveNull(resmanInfo),
    })
  }

  private setEntrata = () => {
    const entrataInfo = this.props.firmInfo.entrataInfoWithObfuscatedSecrets
    this.setState({
      tab: Tab.ENTRATA,
      creds: stringifyAndRemoveNull(entrataInfo),
    })
  }

  private fetchOnEdit = (firmId: number, fetchKey: Tab) => {
    if (fetchKey === Tab.YARDI) {
      return this.fetchYardi(firmId)
    } else if (fetchKey === Tab.ENTRATA) {
      return this.fetchEntrata(firmId)
    } else {
      return Promise.resolve()
    }
  }

  private fetchEntrata = (firmId: number) =>
    apolloWebClient
      .query<FetchFirmEntrataInfoQuery, FetchFirmEntrataInfoQueryVariables>({
        query: fetchFirmEntrataInfo,
        variables: { firmId },
        fetchPolicy: NETWORK_ONLY_FETCH_POLICY,
      })
      .then(res => stringifyAndRemoveNull(res.data.entrataInfo))

  private fetchYardi = (firmId: number) =>
    apolloWebClient
      .query<FetchFirmYardiInfoQuery, FetchFirmYardiInfoQueryVariables>({
        query: fetchFirmYardiInfo,
        variables: { firmId },
        fetchPolicy: NETWORK_ONLY_FETCH_POLICY,
      })
      .then(res => stringifyAndRemoveNull(res.data.yardiInfo))

  private mutateOnSubmit = (firmId: number, fetchKey: Tab, value: string) => {
    if (fetchKey === Tab.RESMAN) {
      return this.updateResmanInfo(firmId, value)
    } else if (fetchKey === Tab.APPFOLIO) {
      return this.updateApmInfo(firmId, value)
    } else if (fetchKey === Tab.ENTRATA) {
      return this.updateEntrataInfo(firmId, value)
    } else if (fetchKey === Tab.YARDI) {
      return this.updateYardiInfo(firmId, value)
    } else {
      return Promise.resolve()
    }
  }

  private updateResmanInfo = (firmId: number, value: string) => {
    const resmanInfo = JSON.parse(value)
    return apolloWebClient.mutate({
      mutation: updateFirmResmanInfo,
      variables: { firmId, resmanInfo },
    })
  }

  private updateApmInfo = (firmId: number, value: string) => {
    const apmInfo = JSON.parse(value)
    return apolloWebClient.mutate({
      mutation: updateFirmApmInfo,
      variables: { firmId, apmInfo },
    })
  }

  private updateEntrataInfo = (firmId: number, value: string) => {
    const entrataInfo = JSON.parse(value)
    return apolloWebClient.mutate({
      mutation: updateFirmEntrataInfo,
      variables: { firmId, entrataInfo },
    })
  }

  private updateYardiInfo = (firmId: number, value: string) => {
    const yardiInfo = JSON.parse(value)
    return apolloWebClient.mutate({
      mutation: updateFirmYardiInfo,
      variables: { firmId, yardiInfo },
    })
  }
}
